import React, { useEffect, useRef, useState } from "react";
import Icon from "./Icon";
import "./SortMenu.css";

const ICONS = {
  featured: "M11 4l1.8 5.2L18 11l-5.2 1.8L11 18l-1.8-5.2L4 11l5.2-1.8zM18 3v4M16 5h4",
  "price-asc": "M7 17V5M3 9l4-4 4 4M14 7h7M14 12h5M14 17h3",
  "price-desc": "M7 5v12M3 13l4 4 4-4M14 7h3M14 12h5M14 17h7",
  rating: "M12 4l2.4 5 5.6.6-4.2 3.8 1.2 5.6L12 16.2 7 19l1.2-5.6L4 9.6 9.6 9z",
};

// A custom listbox for "Sort by": styled like the rest of QKart, keyboard friendly
// (arrows, Home/End, Enter/Space, Escape, type-ahead) and announced correctly by screen readers.
let uid = 0;

const SortMenu = ({ options, value, onChange }) => {
  const id = useRef(`sort-${++uid}`).current;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));
  const selected = options[selectedIndex];

  const openMenu = (index = selectedIndex) => {
    setActive(index);
    setOpen(true);
  };
  const close = (focusButton = true) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  };
  const choose = (index) => {
    onChange(options[index].value);
    close();
  };

  useEffect(() => {
    if (!open) return undefined;
    listRef.current?.focus();
    const onPointer = (e) => {
      if (!rootRef.current?.contains(e.target)) close(false);
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onButtonKey = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      openMenu(selectedIndex);
    }
  };

  const onListKey = (e) => {
    const last = options.length - 1;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((i) => (i >= last ? 0 : i + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((i) => (i <= 0 ? last : i - 1));
        break;
      case "Home":
        e.preventDefault();
        setActive(0);
        break;
      case "End":
        e.preventDefault();
        setActive(last);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        choose(active);
        break;
      case "Escape":
        e.preventDefault();
        close();
        break;
      case "Tab":
        close(false);
        break;
      default:
        if (e.key.length === 1) {
          const k = e.key.toLowerCase();
          const next = options.findIndex((o, i) => i > active && o.label.toLowerCase().startsWith(k));
          const first = options.findIndex((o) => o.label.toLowerCase().startsWith(k));
          if (next !== -1 || first !== -1) setActive(next !== -1 ? next : first);
        }
    }
  };

  return (
    <div className={`sort-menu ${open ? "is-open" : ""}`} ref={rootRef}>
      <span className="sort-menu-label" id={`${id}-label`}>
        Sort by
      </span>
      <button
        ref={buttonRef}
        type="button"
        className="sort-menu-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${id}-label ${id}-value`}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onButtonKey}
      >
        <Icon path={ICONS[selected.value]} size={16} className="sort-menu-lead" />
        <span id={`${id}-value`} className="sort-menu-value">
          {selected.label}
        </span>
        <Icon path="M6 9l6 6 6-6" size={16} strokeWidth={2.2} className="sort-menu-chevron" />
      </button>
      {open && (
        <ul
          ref={listRef}
          className="sort-menu-list"
          role="listbox"
          tabIndex={-1}
          aria-labelledby={`${id}-label`}
          aria-activedescendant={`${id}-opt-${active}`}
          onKeyDown={onListKey}
        >
          {options.map((o, i) => {
            const isSelected = o.value === value;
            return (
              <li
                key={o.value}
                id={`${id}-opt-${i}`}
                role="option"
                aria-selected={isSelected}
                className={`sort-menu-option ${i === active ? "is-active" : ""} ${isSelected ? "is-selected" : ""}`}
                onPointerEnter={() => setActive(i)}
                onClick={() => choose(i)}
              >
                <span className="sort-menu-icon">
                  <Icon path={ICONS[o.value]} size={16} />
                </span>
                <span className="sort-menu-text">{o.label}</span>
                {isSelected && <Icon name="check" size={16} strokeWidth={2.6} className="sort-menu-check" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SortMenu;
